import { onlyDigits6 } from './redeemConsentPresentation';
const MIN_POLL_MS = 1500;
const NETWORK_ERROR = 'Could not reach the server. Check the connection and try again.';
/** API adapters should return results, but a thrown network error must never leave the step stuck. */
async function safe(call) {
    try {
        return await call();
    }
    catch (error) {
        const message = error === null || error === void 0 ? void 0 : error.message;
        return { ok: false, message: message && message.trim() ? message : NETWORK_ERROR };
    }
}
/**
 * Framework-free state for the staff "Waiting for Player" step: polls the request while it is
 * pending (so an in-app approval shows up by itself), and runs verify / resend / cancel.
 * The React hook (useRedeemConsent) only subscribes to it.
 */
export class RedeemConsentController {
    constructor(opts) {
        this.listeners = new Set();
        this.pollHandle = null;
        this.stopped = true;
        this.approvedFired = false;
        this.getState = () => this.state;
        this.subscribe = (listener) => {
            this.listeners.add(listener);
            return () => {
                this.listeners.delete(listener);
            };
        };
        this.opts = {
            setTimer: (fn, ms) => setTimeout(fn, ms),
            clearTimer: (h) => clearTimeout(h),
            ...opts,
        };
        this.state = { request: opts.initialRequest, code: '', busy: null, error: null };
    }
    start() {
        if (!this.stopped)
            return;
        this.stopped = false;
        this.schedulePoll();
    }
    stop() {
        this.stopped = true;
        if (this.pollHandle != null)
            this.opts.clearTimer(this.pollHandle);
        this.pollHandle = null;
    }
    setCode(value) {
        this.setState({ code: onlyDigits6(value), error: null });
    }
    /** Fetches the latest state now (e.g. when the countdown reaches zero). */
    async refresh() {
        const res = await safe(() => this.opts.api.get(this.state.request.id));
        if (res.ok)
            this.applyRequest(res.data.request, null);
    }
    async verify() {
        const code = onlyDigits6(this.state.code);
        if (code.length !== 6 || this.state.busy || this.state.request.status !== 'pending')
            return;
        this.setState({ busy: 'verify', error: null });
        const res = await safe(() => this.opts.api.verify(this.state.request.id, code));
        if (res.ok) {
            this.setState({ busy: null, code: '' });
            this.applyRequest(res.data.request, res.data.redeem);
            return;
        }
        this.setState({ busy: null, error: res.message, code: res.code === 'INVALID_REDEEM_CODE' ? '' : this.state.code });
        if (res.request)
            this.applyRequest(res.request, null);
    }
    async resend() {
        if (this.state.busy || this.state.request.status !== 'pending')
            return;
        this.setState({ busy: 'resend', error: null });
        const res = await safe(() => this.opts.api.resend(this.state.request.id));
        this.setState({ busy: null, error: res.ok ? null : res.message });
        if (res.ok)
            this.applyRequest(res.data.request, null);
        else if (res.request)
            this.applyRequest(res.request, null);
    }
    async cancel() {
        if (this.state.busy || this.state.request.status !== 'pending')
            return;
        this.setState({ busy: 'cancel', error: null });
        const res = await safe(() => this.opts.api.cancel(this.state.request.id));
        this.setState({ busy: null, error: res.ok ? null : res.message });
        if (res.ok)
            this.applyRequest(res.data.request, null);
    }
    applyRequest(request, redeem) {
        var _a, _b, _c, _d;
        this.setState({ request });
        (_b = (_a = this.opts).onRequestChange) === null || _b === void 0 ? void 0 : _b.call(_a, request);
        if (request.status === 'approved' && !this.approvedFired) {
            this.approvedFired = true;
            (_d = (_c = this.opts).onApproved) === null || _d === void 0 ? void 0 : _d.call(_c, request, redeem);
        }
        if (request.status !== 'pending')
            this.stop();
    }
    schedulePoll() {
        if (this.stopped || this.state.request.status !== 'pending')
            return;
        const ms = Math.max(MIN_POLL_MS, Number(this.state.request.poll_after_ms) || 3000);
        this.pollHandle = this.opts.setTimer(() => {
            void this.pollOnce();
        }, ms);
    }
    async pollOnce() {
        this.pollHandle = null;
        if (this.stopped)
            return;
        // Skip a beat while the staff member is acting; the action's own response updates the state.
        if (!this.state.busy) {
            const res = await safe(() => this.opts.api.get(this.state.request.id));
            if (!this.stopped && res.ok)
                this.applyRequest(res.data.request, null);
        }
        this.schedulePoll();
    }
    setState(patch) {
        this.state = { ...this.state, ...patch };
        this.listeners.forEach((l) => l());
    }
}
